import { createContext, useContext, useMemo, useState, useCallback } from 'react';

const ToastContext = createContext({ addToast: () => {}, removeToast: () => {} });

let toastCounter = 0;
const DEFAULT_TOAST = {
    title: 'Notification',
    description: '',
    type: 'info',
    duration: 4000,
};

function ToastContainer({ toasts, removeToast }) {
    return (
        <div className="toast-container" role="status" aria-live="polite">
            {toasts.map((toast) => (
                <div key={toast.id} className={`toast toast--${toast.type}`}>
                    <div className="toast__content">
                        {toast.title ? <strong className="toast__title">{toast.title}</strong> : null}
                        {toast.description ? <span className="toast__description">{toast.description}</span> : null}
                    </div>
                    <button type="button" className="toast__close" onClick={() => removeToast(toast.id)} aria-label="Dismiss notification">
                        ×
                    </button>
                </div>
            ))}
        </div>
    );
}

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const removeToast = useCallback((id) => {
        setToasts((current) => current.filter((toast) => toast.id !== id));
    }, []);

    const addToast = useCallback((toast) => {
        const id = ++toastCounter;
        const merged = { ...DEFAULT_TOAST, ...toast, id };
        setToasts((current) => [...current, merged]);
        const timeout = setTimeout(() => {
            removeToast(id);
        }, merged.duration);
        return () => {
            clearTimeout(timeout);
            removeToast(id);
        };
    }, [removeToast]);

    const value = useMemo(() => ({ addToast, removeToast }), [addToast, removeToast]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
}

export function useToast() {
    return useContext(ToastContext);
}
